'use client'

import { FaGithub } from 'react-icons/fa'
import { Menu, X } from 'lucide-react'
import React, { useState, useEffect } from 'react'

import { cn } from '../../lib/cn'
import { useLink } from '../../lib/link-context'
import { NoiseSvgFilter } from '../../lib/noise-filter'

import {
  Button,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@mdxui/primitives'

interface NavItem {
  name: string
  link: string
}

interface Logo {
  /** Link target @default '/' */
  url?: string
  /** Logo image — omit for a text-only wordmark */
  src?: string
  /** Image alt text @default title */
  alt?: string
  /** Wordmark text, shown next to the image (or alone when src is omitted) */
  title?: string
}

/**
 * CTA button configuration.
 * Supports both URL-based navigation and onClick handlers.
 * When both are provided, onClick takes precedence.
 */
interface CTA {
  text: string
  /** URL for navigation (optional if onClick is provided) */
  url?: string
  /** Click handler - takes precedence over url when provided */
  onClick?: () => void
}

/**
 * Controls the navbar background appearance when scrolled.
 * - `translucent`: Semi-transparent with backdrop blur (default). Best for clean backgrounds.
 * - `solid`: Fully opaque background. Use when page has noise texture or busy backgrounds
 *   that would show through the translucent backdrop.
 */
export type NavbarAppearance = 'translucent' | 'solid'

export interface NavbarSimpleProps {
  /**
   * Brand mark on the left: pass a plain string for a text-only wordmark
   * (`logo="acme.do"`), or a Logo object for image + text.
   */
  logo?: string | Logo
  menu?: NavItem[]
  githubUrl?: string
  primaryCta?: CTA
  secondaryCta?: CTA
  /**
   * Controls the navbar background appearance when scrolled.
   * - `translucent`: Semi-transparent with backdrop blur (default). Best for clean backgrounds.
   * - `solid`: Fully opaque background. Use when page has noise texture or busy backgrounds.
   * @default 'translucent'
   */
  appearance?: NavbarAppearance
  /**
   * Enable noise texture blending for the navbar background.
   * When true, the navbar renders its own noise layer that matches the page's NoiseTexture.
   * Use this when the page has a non-fixed NoiseTexture and you want the navbar to blend
   * seamlessly instead of appearing as a clean bar over noisy content.
   *
   * Note: This only applies when the navbar has a visible background (when scrolled).
   * @default false
   *
   * @example
   * ```tsx
   * <NoiseTexture /> {/* Non-fixed, scrolls with page *\/}
   * <NavbarSimple noiseBlend />
   * <Hero />
   * ```
   */
  noiseBlend?: boolean
  className?: string
}

/**
 * Derive a brand monogram from a wordmark: the first letters of up to 2 words,
 * uppercased (e.g. "Carriage Donation" → "CD", "Ledger" → "L"). Used for the
 * subtle text-logo logomark when no image `src` is supplied.
 */
const deriveInitials = (title: string): string =>
  title
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()

const defaultLogo: Logo = {
  url: '/',
  src: '/logos/mdxui.svg',
  alt: 'Logo',
  title: 'MDXUI',
}

const defaultMenu: NavItem[] = [
  { name: 'API', link: '#' },
  { name: 'CLI', link: '#' },
  { name: 'SDK', link: '#' },
  { name: 'MCP', link: '#' },
  { name: 'Docs', link: '#' },
]

const defaultPrimaryCta: CTA = {
  text: 'Sign Up',
  url: '/signup',
}

const NavbarSimple = ({
  logo = defaultLogo,
  menu = defaultMenu,
  githubUrl,
  primaryCta = defaultPrimaryCta,
  secondaryCta,
  appearance = 'translucent',
  noiseBlend = false,
  className,
}: NavbarSimpleProps) => {
  const Link = useLink()
  const [activeItem, setActiveItem] = useState('')
  const [isScrolled, setIsScrolled] = useState(false)

  // Normalize the string shorthand to a text-only Logo
  const brand: Logo = typeof logo === 'string' ? { title: logo } : logo

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    handleScroll() // Check initial state
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Show noise when noiseBlend is enabled AND navbar has a visible background
  const showNoise = noiseBlend && isScrolled

  return (
    <section
      className={cn(
        'sticky top-0 z-50 py-4 transition-[background-color,border-color,backdrop-filter] duration-150 ease-out',
        isScrolled
          ? appearance === 'solid'
            ? 'border-b border-border/70 bg-background'
            : 'border-b border-border/70 bg-background/90 backdrop-blur-sm'
          : 'border-b border-transparent bg-transparent',
        className
      )}
    >
      {/* Noise overlay for the navbar - matches page NoiseTexture */}
      {showNoise && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <NoiseSvgFilter />
        </div>
      )}

      <nav className="container relative mx-auto flex max-w-7xl items-center justify-between">
        {/* Left Logo or WordMark */}
        <Link href={brand.url ?? '/'} className="flex items-center gap-2">
          {brand.src ? (
            <span className="inline-flex rounded-[var(--radius)] bg-accent/50">
              <img src={brand.src} className="dark:invert" alt={brand.alt ?? brand.title ?? 'Logo'} />
            </span>
          ) : (
            // Text-logo case: subtle brand logomark (monogram) before the
            // wordmark, so a text-only brand still reads as a logo+wordmark
            // rather than bare text. Token-only (--primary), tasteful monogram.
            brand.title && (
              <span
                aria-hidden
                className="inline-flex size-7 items-center justify-center rounded-[var(--radius)] bg-primary/10 text-[0.7rem] font-semibold leading-none tracking-tight text-primary"
              >
                {deriveInitials(brand.title)}
              </span>
            )
          )}
          {brand.title && (
            <span className="text-lg font-semibold text-foreground">
              {brand.title}
            </span>
          )}
        </Link>

        <NavigationMenu className="hidden lg:block">
          <NavigationMenuList className="flex items-center gap-6">
            {menu.map((item) => (
              <React.Fragment key={item.name}>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      href={item.link}
                      onClick={() => { setActiveItem(item.name) }}
                      className={cn(
                        'relative cursor-pointer text-sm font-medium bg-transparent hover:bg-transparent focus:bg-transparent dark:hover:bg-transparent dark:focus:bg-transparent hover:text-foreground',
                        activeItem === item.name
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      )}
                    >
                      {item.name}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </React.Fragment>
            ))}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Mobile Menu Popover */}
        <MobileNav
          menu={menu}
          primaryCta={primaryCta}
          secondaryCta={secondaryCta}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
        />

        <div className="hidden items-center gap-4 lg:flex">
          {githubUrl && (
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <FaGithub className="size-5" />
            </a>
          )}
          {secondaryCta && (
            secondaryCta.onClick ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 py-2.5 text-sm font-normal"
                onClick={secondaryCta.onClick}
              >
                {secondaryCta.text}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 py-2.5 text-sm font-normal"
                asChild
              >
                <Link href={secondaryCta.url!}>{secondaryCta.text}</Link>
              </Button>
            )
          )}
          {primaryCta.onClick ? (
            <Button
              variant="outline"
              size="sm"
              className="h-10 py-2.5 text-sm font-normal"
              onClick={primaryCta.onClick}
            >
              {primaryCta.text}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-10 py-2.5 text-sm font-normal"
              asChild
            >
              <Link href={primaryCta.url!}>{primaryCta.text}</Link>
            </Button>
          )}
        </div>
      </nav>
    </section>
  )
}

export { NavbarSimple }

const AnimatedHamburger = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <div className="group relative size-full">
      <div className="absolute flex size-full items-center justify-center">
        <Menu
          className={cn(
            'absolute size-6 text-muted-foreground transition-all duration-300 group-hover:text-foreground',
            isOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'
          )}
        />
        <X
          className={cn(
            'absolute size-6 text-muted-foreground transition-all duration-300 group-hover:text-foreground',
            isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
          )}
        />
      </div>
    </div>
  )
}

const MobileNav = ({
  menu,
  primaryCta,
  secondaryCta,
  activeItem,
  setActiveItem,
}: {
  menu: NavItem[]
  primaryCta: CTA
  secondaryCta?: CTA
  activeItem: string
  setActiveItem: (item: string) => void
}) => {
  const Link = useLink()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="flex h-full items-center lg:hidden">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon">
            <AnimatedHamburger isOpen={isOpen} />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          sideOffset={12}
          collisionPadding={16}
          className="block w-[calc(100vw-32px)] overflow-hidden rounded-[var(--radius-xl,calc(var(--radius)*1.5))] p-0 lg:hidden"
        >
          <ul className="w-full bg-background py-4 text-foreground">
            {menu.map((navItem) => (
              <li key={navItem.name}>
                <Link
                  href={navItem.link}
                  onClick={() => setActiveItem(navItem.name)}
                  className={cn(
                    'flex items-center border-l-[3px] px-6 py-4 text-sm font-medium',
                    activeItem === navItem.name
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {navItem.name}
                </Link>
              </li>
            ))}
            <li className="flex flex-col gap-2 px-7 py-2">
              {primaryCta.onClick ? (
                <Button variant="outline" onClick={primaryCta.onClick}>
                  {primaryCta.text}
                </Button>
              ) : (
                <Button variant="outline" asChild>
                  <Link href={primaryCta.url!}>{primaryCta.text}</Link>
                </Button>
              )}
              {secondaryCta && (
                secondaryCta.onClick ? (
                  <Button variant="ghost" onClick={secondaryCta.onClick}>
                    {secondaryCta.text}
                  </Button>
                ) : (
                  <Button variant="ghost" asChild>
                    <Link href={secondaryCta.url!}>{secondaryCta.text}</Link>
                  </Button>
                )
              )}
            </li>
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  )
}
